import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "../../structures/Footer";
import "../../../assets/css/payment.css";

export default function PaymentPage() {
  const navigate = useNavigate();

  const [isProcessing, setIsProcessing] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [timeLeft, setTimeLeft] = useState(3600); // 1 hours countdown
  const [paymentInstructions, setPaymentInstructions] = useState(null);

  const API_BASE = process.env.REACT_APP_BACKEND_API;

  // Ambil data booking dari LocalStorage (Pindahkan ke atas agar bisa dipakai di Hook)
  const [bookingData] = useState(() => {
    const saved = localStorage.getItem("bookingData");
    return saved ? JSON.parse(saved) : null;
  });

  const { name, email, phone, pax, date, time, totalPrice, originalPrice, discount, bookingId, experience, addons } = bookingData || {};

  const mainExpPrice = experience ? (() => {
    let price = experience.price || 0;
    if (experience.id === 'sailing') {
      return price * Math.max(pax || 1, 5);
    }
    if (experience.id === 'kayak') {
      return price;
    }
    let total = price * (pax || 1);
    if (experience.id === 'bicycle' && bookingData?.withDrone) {
      total += 300000;
    }
    return total;
  })() : 0;

  const getAddonCost = (addon) => {
    let addonPax = addon.pax || 1;
    if (addon.id === 'sailing') addonPax = Math.max(addonPax, 5);
    if (addon.id === 'kayak') {
      return addon.price * addonPax; // price is per boat
    } else if (addon.id === 'prewedding') {
      return addon.price;
    } else {
      return addon.price * addonPax;
    }
  };

  // Scroll animation
  useEffect(() => {
    const animateElements = document.querySelectorAll('.animate-on-scroll');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate');
        }
      });
    }, { threshold: 0.1 });

    animateElements.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Payment countdown timer
  useEffect(() => {
    if (timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          const isMantos = bookingData?.experience?.id?.startsWith('mantos-');
          navigate(`${isMantos ? '/mantos/booking/failed' : '/booking/failed'}?bookingId=${bookingId || ""}`);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, navigate, bookingId, bookingData]);

  useEffect(() => {
    if (isPaid || (!isProcessing && !paymentInstructions) || !bookingId) return;

    const interval = setInterval(() => {
      checkPaymentStatus();
    }, 3000); // cek tiap 3 detik

    return () => clearInterval(interval);
  }, [isProcessing, isPaid, paymentInstructions, bookingId]);


  // Load Doku & Midtrans Scripts
  useEffect(() => {
    // Doku Script
    const dokuScript = document.createElement('script');
    dokuScript.src = "https://sandbox.doku.com/joker-checkout/js/doku.js";
    dokuScript.async = true;
    document.body.appendChild(dokuScript);

    // Midtrans Snap Script
    const midtransScript = document.createElement('script');
    midtransScript.src = "https://app.sandbox.midtrans.com/snap/snap.js";
    midtransScript.setAttribute('data-client-key', "Mid-client-a61RcWuPtD5QLy-f"); // From your .env
    midtransScript.async = true;
    document.body.appendChild(midtransScript);

    return () => {
      document.body.removeChild(dokuScript);
      document.body.removeChild(midtransScript);
    }
  }, []);


  // Format countdown time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };





  // Validate booking from API & Calculate remaining time
  const validateBooking = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/bookings/${bookingId}`, {
        headers: { "ngrok-skip-browser-warning": "true" }
      });
      const data = await res.json();

      if (!data.success || !data.data || data.data.status === "FAILED") {
        localStorage.removeItem("bookingData");
        localStorage.removeItem("snapToken");
        const isMantos = bookingData?.experience?.id?.startsWith('mantos-');
        if (data.data?.status === "FAILED") {
           navigate(`${isMantos ? '/mantos/booking/failed' : '/booking/failed'}?bookingId=${bookingId || ""}`);
        } else {
           alert("Booking not found or expired. Please create a new booking.");
           navigate(`${isMantos ? '/mantos/booking/failed' : '/booking/failed'}?bookingId=${bookingId || ""}`);
        }
        return false;
      }

      // HITUNG SISA WAKTU (1 JAM DARI CREATED AT)
      const createdAt = new Date(data.data.createdAt);
      const now = new Date();
      const diffInSeconds = Math.floor((now - createdAt) / 1000);
      const remaining = Math.max(0, 3600 - diffInSeconds);
      
      setTimeLeft(remaining);
      return true;
    } catch (err) {
      console.error("Validation error:", err);
      return false;
    }
  };

  // Initial validation to sync timer
  useEffect(() => {
    if (bookingId) {
      validateBooking();
    }
  }, [bookingId]);

  const checkPaymentStatus = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/bookings/${bookingId}`, {
        headers: { "ngrok-skip-browser-warning": "true" }
      });
      const data = await res.json();

if (data.success && data.data.status === "PAID") {
        setIsPaid(true);
        localStorage.removeItem("snapToken");

        // Redirect dinamis berdasarkan experience
        const isMantos = bookingData?.experience?.id?.startsWith('mantos-');
        navigate(isMantos ? "/mantos/booking/success" : "/booking/success", {
          state: {
            ...bookingData,
            status: "PAID",
          },
        });
      }
    } catch (error) {
      console.error("Failed to check payment status", error);
    }
  };


  // Handle payment submission - Direct to Midtrans

  const handlePaymentSubmit = async () => {
    setIsProcessing(true);

    // Validate booking before start payment flow
    if (!await validateBooking()) {
      setIsProcessing(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/payment/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true"
        },
        body: JSON.stringify({ 
          bookingId,
          frontendUrl: window.location.origin // MENGIRIM URL ASAL (Localhost atau Production)
        }),
      });

      const data = await res.json();

      if (!data.success) {
        alert("Payment failed: " + (data.error || "Unknown error"));
        setIsProcessing(false);
        return;
      }

      // DETEKSI GATEWAY (Midtrans vs Doku)
      const dokuData = data.data || data;

      // 1. Cek apakah ini Midtrans Snap Token
      const snapToken = dokuData.token || dokuData.snapToken;

      // 2. Cek apakah ini Doku URL
      const paymentUrl =
        dokuData?.response?.payment?.url ||
        dokuData?.payment?.url ||
        dokuData?.payment_url ||
        dokuData?.url ||
        dokuData?.data?.url;

      if (snapToken) {
        console.log("Triggering Midtrans Snap Modal...");
        if (window.snap) {
          window.snap.pay(snapToken);
        } else {
          alert("Midtrans Snap script not loaded yet. Please try again in a moment.");
        }
      } else if (paymentUrl) {
        console.log("Triggering Doku Checkout Modal...");
        if (window.loadDokuCheckout) {
          window.loadDokuCheckout(paymentUrl);
        } else {
          console.warn("Doku JS not loaded yet, redirecting...");
          window.location.href = paymentUrl;
        }
      } else {
        console.error("Unknown Payment Response Structure:", data);
        alert("Payment failed: Could not find valid token (Midtrans) or URL (Doku) in response.");
      }

      // Save for fallback instructions if needed
      setPaymentInstructions(dokuData);
      setIsProcessing(false);

    } catch (err) {
      console.error(err);
      alert("Server Error, Try Again!");
      setIsProcessing(false);
    }
  };





  // Session expired check (Moved to bottom to follow Rules of Hooks)
  if (timeLeft === 0) {
    return null;
  }

  // Safety check for missing data
  if (!bookingData) {
    return (
      <div className="payment-page-container">
        <div className="loading-state">
          <p>Redirecting to reservation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-page-container">
      {/* Header Section */}
      <section className="payment-header animate-on-scroll">
        <div className="payment-header-content">
          <div className="header-ornament">◆</div>
          <h1 className="payment-title">Complete Your Payment</h1>
          <p className="payment-subtitle">Secure your booking with a safe payment</p>

          {/* Countdown Timer */}
          <div className="payment-timer">
            <span className="timer-icon">⏱️</span>
            <span className="timer-label">Complete payment within:</span>
            <span className={`timer-value ${timeLeft <= 5 ? 'urgent' : ''}`}>
              {formatTime(timeLeft)}
            </span>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="payment-content">
        <div className="payment-content-wrapper">

          {/* Payment Summary Card */}
          <div className="payment-summary-card animate-on-scroll">
            <div className="summary-header">
              <h2>Order Summary</h2>
              <div className="booking-id-badge">
                <span className="badge-label">Booking ID</span>
                <span className="badge-value">{bookingId}</span>
              </div>
            </div>

            {/* Experience Details */}
            <div className="summary-card">
              <div className="experience-info">
                <span className="experience-icon">{experience.icon}</span>
                <div className="experience-details">
                  <h3>{(experience && experience.name) || bookingData.experienceName || 'Experience'}</h3>
                  {addons && addons.length > 0 && (
                    <div style={{ marginTop: '8px', fontSize: '13px', color: '#64748b', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span style={{ fontWeight: 600, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8' }}>Addons:</span>
                      {addons.map(addon => (
                        <div key={addon.id} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span>{addon.emoji}</span>
                          <span>{addon.name} ({addon.pax} {addon.id === 'kayak' ? (addon.pax > 1 ? 'boats' : 'boat') : (addon.pax > 1 ? 'pax' : 'pax')})</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="booking-details">
                <div className="detail-row">
                  <span className="detail-icon">👤</span>
                  <span className="detail-label">Guest Name</span>
                  <span className="detail-value">{name}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-icon">📧</span>
                  <span className="detail-label">Email</span>
                  <span className="detail-value">{email}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-icon">📱</span>
                  <span className="detail-label">Phone</span>
                  <span className="detail-value">{phone}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-icon">👥</span>
                  <span className="detail-label">Number of Guests</span>
                  <span className="detail-value">{pax} {pax > 1 ? 'people' : 'person'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-icon">📅</span>
                  <span className="detail-label">Date</span>
                  <span className="detail-value">
                    {new Date(date).toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-icon">🕐</span>
                  <span className="detail-label">Time</span>
                  <span className="detail-value">{time}</span>
                </div>
              </div>
            </div>

            {/* Price Breakdown */}
            <div className="price-breakdown">
              <h4>Price Details</h4>
              
              {/* Main Experience Row */}
              <div className="price-row">
                <span>{(experience && experience.name) || bookingData?.experienceName || 'Experience'} ({pax}x)</span>
                <span>IDR {mainExpPrice.toLocaleString()}</span>
              </div>

              {/* Addons Rows */}
              {addons && addons.map(addon => (
                <div key={addon.id} className="price-row">
                  <span>{addon.name} ({addon.pax}x)</span>
                  <span>IDR {getAddonCost(addon).toLocaleString()}</span>
                </div>
              ))}

              {discount > 0 && (
                <div className="price-row promo-discount">
                  <span>🎁 Promo Discount</span>
                  <span className="discount-amount">- IDR {discount.toLocaleString()}</span>
                </div>
              )}

              <div className="price-divider"></div>
              <div className="price-row total">
                <span>Total Payment</span>
                <span className="total-amount">IDR {totalPrice.toLocaleString()}</span>
              </div>
            </div>

            {/* Payment Actions */}

            <div className="payment-actions">
              {!paymentInstructions ? (
                <button
                  className="pay-btn primary"
                  disabled={isProcessing}
                  onClick={handlePaymentSubmit}
                >
                  {isProcessing ? (
                    <>
                      <span className="spinner"></span>
                      <span>Opening Payment Gateway...</span>
                    </>
                  ) : (
                    <>
                      <span className="btn-icon">💳</span>
                      <span>Pay Now - IDR {totalPrice.toLocaleString()}</span>
                      <span className="btn-arrow">→</span>
                    </>
                  )}
                </button>
              ) : null}

              <button
                className="back-btn"
                onClick={() => navigate(-1)}
                disabled={isProcessing}
              >
                <span className="btn-icon">←</span>
                <span>Back to Booking Form</span>
              </button>
            </div>

          </div>

          {/* Payment Methods Preview (Right Side) */}
          <div className="payment-methods-preview animate-on-scroll">
            {/* Payment Process Steps */}
            <div className="payment-steps">
              <h3>How Payment Works</h3>
              <div className="steps-list">
                <div className="step-item">
                  <span className="step-number">1</span>
                  <div className="step-content">
                    <h4>Click "Pay Now"</h4>
                    <p>Proceed to secure payment gateway</p>
                  </div>
                </div>
                <div className="step-item">
                  <span className="step-number">2</span>
                  <div className="step-content">
                    <h4>Choose Payment Method</h4>
                    <p>Select your preferred payment option</p>
                  </div>
                </div>
                <div className="step-item">
                  <span className="step-number">3</span>
                  <div className="step-content">
                    <h4>Complete Payment</h4>
                    <p>Follow instructions to finalize payment</p>
                  </div>
                </div>
                <div className="step-item">
                  <span className="step-number">4</span>
                  <div className="step-content">
                    <h4>Get Invoice</h4>
                    <p>Receive invoice via PDF and WhatsApp</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}